import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import Swal from "sweetalert2";

import { UpdateUser } from "../utils/usersAPI";
import { setLoggedUser } from "../features/userSlice";
import { UpdateUserAddress } from "../utils/userAddressAPI";

const UserDetailsSection = () => {

  const dispatch = useDispatch();
  const loggedUser = useSelector((state) => state.user.logedUser);

  const [isEditing, setIsEditing] = useState(false);
  const [userForm, setUserForm] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    street: "",
    houseNumber: "",
    apartment: "",
    floor: "",
    entrance: "",
    notes: "",
  });

  // כשנטען המשתמש מהרדאקס – למלא את הטופס
  useEffect(() => {
    if (loggedUser) {
      const address = loggedUser.userAddresses?.[0];

      setUserForm({
        name: loggedUser.name || "",
        email: loggedUser.email || "",
        phone: loggedUser.phone || "",
        city: address?.city || "",
        street: address?.street || "",
        houseNumber: address?.houseNumber || "",
        apartment: address?.apartment || "",
        floor: address?.floor || "",
        entrance: address?.entrance || "",
        notes: address?.notes || "",
      });
    }
  }, [loggedUser]);

  const handleUserFieldChange = (e) => {
    const { name, value } = e.target;
    setUserForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleStartEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    if (loggedUser) {
      const address = loggedUser.userAddresses?.[0];

      setUserForm({
        name: loggedUser.name || "",
        email: loggedUser.email || "",
        phone: loggedUser.phone || "",
        city: address?.city || "",
        street: address?.street || "",
        houseNumber: address?.houseNumber || "",
        apartment: address?.apartment || "",
        floor: address?.floor || "",
        entrance: address?.entrance || "",
        notes: address?.notes || "",
      });
    }

    setIsEditing(false);
  };
  const requiredFieldsFilled =
    userForm.name.trim() &&
    userForm.email.trim() &&
    userForm.phone.trim() &&
    userForm.city.trim() &&
    userForm.street.trim() &&
    String(userForm.houseNumber).trim();

  const handleSave = async () => {
    try {
      const currentAddress = loggedUser.userAddresses?.[0];
      if (!currentAddress) {
        Swal.fire({
          title: "לא נמצאה כתובת לעדכון",
          icon: "warning",
        });
        return;
      }

      const updatedUser = {
        id: loggedUser.id,
        name: userForm.name,
        email: userForm.email,
        phone: userForm.phone,
        address: loggedUser.address || "",
        tipeUser: loggedUser.tipeUser,
        hasPermanentDiscount: loggedUser.hasPermanentDiscount,
      };

      const updatedAddress = {
        ...currentAddress,
        userId: loggedUser.id,
        city: userForm.city,
        street: userForm.street,
        houseNumber: userForm.houseNumber,
        apartment: userForm.apartment,
        floor: userForm.floor,
        entrance: userForm.entrance,
        notes: userForm.notes,
        isDefault: currentAddress?.isDefault ?? true,
      };

      await UpdateUser(loggedUser.id, updatedUser);

      const addressRes = await UpdateUserAddress(
        currentAddress.id,
        updatedAddress
      );

      dispatch(
        setLoggedUser({
          ...loggedUser,
          ...updatedUser,
          userAddresses: [addressRes.data],
        })
      );

      Swal.fire({
        title: "הפרטים עודכנו בהצלחה",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });

      setIsEditing(false);
    } catch (err) {
      console.error("שגיאה בעדכון פרטי המשתמש:", err);
      Swal.fire({
        title: "שגיאה בעדכון פרטי המשתמש",
        icon: "error",
      });
    }
  };

  if (!loggedUser) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography variant="h6">פרטי לקוח</Typography>

        {!isEditing ? (
          <IconButton size="small" onClick={handleStartEdit}>
            <EditIcon fontSize="small" />
          </IconButton>
        ) : (
          <IconButton size="small" onClick={handleCancelEdit}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {!isEditing && (
        <>
          <Typography>שם: {loggedUser.name}</Typography>
          <Typography>אימייל: {loggedUser.email}</Typography>
          <Typography>טלפון: {loggedUser.phone}</Typography>
          <Typography>
            כתובת למשלוח: {
              loggedUser.userAddresses?.length > 0
                ? `${loggedUser.userAddresses[0].city},
         ${loggedUser.userAddresses[0].street}
         ${loggedUser.userAddresses[0].houseNumber}`
                : "לא הוגדרה כתובת"
            }
          </Typography>
        </>
      )}

      {isEditing && (
        <Box sx={{ display: "grid", gap: 1 }}>
          <TextField
            label="שם"
            name="name"
            required
            value={userForm.name}
            onChange={handleUserFieldChange}
            fullWidth
          />
          <TextField
            label="אימייל"
            name="email"
            required
            type="email"
            value={userForm.email}
            onChange={handleUserFieldChange}
            fullWidth
          />
          <TextField
            label="טלפון"
            name="phone"
            required
            value={userForm.phone}
            onChange={handleUserFieldChange}
            fullWidth
          />
          <TextField
            label="עיר"
            name="city"
            required
            value={userForm.city}
            onChange={handleUserFieldChange}
            fullWidth
          />

          <TextField
            label="רחוב"
            name="street"
            required
            value={userForm.street}
            onChange={handleUserFieldChange}
            fullWidth
          />

          <TextField
            label="מספר בניין"
            name="houseNumber"
            required
            value={userForm.houseNumber}
            onChange={handleUserFieldChange}
            fullWidth
          />

          <TextField
            label="דירה (אופציונלי)"
            name="apartment"
            value={userForm.apartment}
            onChange={handleUserFieldChange}
            fullWidth
          />

          <TextField
            label="קומה (אופציונלי)"
            name="floor"
            value={userForm.floor}
            onChange={handleUserFieldChange}
            fullWidth
          />

          <TextField
            label="כניסה (אופציונלי)"
            name="entrance"
            value={userForm.entrance}
            onChange={handleUserFieldChange}
            fullWidth
          />

          <TextField
            label="הערות (אופציונלי)"
            name="notes"
            value={userForm.notes}
            onChange={handleUserFieldChange}
            fullWidth
            multiline
            rows={3}
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1 }}>
            <Button variant="outlined" onClick={handleCancelEdit}>
              ביטול
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!requiredFieldsFilled}
            >
              שמור פרטים
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default UserDetailsSection;
